import { Fragment } from 'react';
import { Link } from 'react-router';

import { approximateDuration, durationBetweenDates, formatDate } from 'my-core/date-utils';
import { trackCustomEvent } from 'my-core/gtm-events';
import { studentCoursePageGradeBoostersRelativePath, studentCoursePageStudySessionPath } from 'my-core/routes';
import {
  getAttachmentTypes,
  getStudySessionInstructor,
  getStudySessionStartTime,
  STUDY_SESSION_TYPES,
} from 'my-core/study-session-utils';
import { getUserDisplayName } from 'my-core/user-utils';
import { pluralize } from 'my-utils';

import CustomColorButton from 'my-elements/CustomColorButton';
import MuiAvatar from 'my-elements/MuiAvatar';

import Divider, { dividerClasses } from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { faCalendar, faClose } from '@fortawesome/pro-regular-svg-icons';
import { faHourglassClock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function GradeBoosterBanner({ assessment, course, onClose, studySessions }) {
  const studySessionTypes = Object.groupBy(studySessions, ss => ss.type);
  const ssTypes = Object.keys(studySessionTypes);
  const hasMultipleTypes = ssTypes.length > 1;
  const allBoostersPath = `../${studentCoursePageGradeBoostersRelativePath(course)}`;
  return (
    <div
      css={({ breakpoints, palette, shape, spacing, typography }) => {
        const mobileCss = {
          padding: spacing(2),
          columnGap: spacing(2),
          rowGap: spacing(1),
          alignItems: 'center',
          '& > .closeBtn': { right: spacing(2), top: spacing(1) },
          '& > .ssImage': { width: 40, height: 40 },
          '& > .info': {
            '& > .title': {
              flex: 1,
              ...typography.h6,
              lineHeight: 1.2,
            },
            display: 'contents',
            '& > .detailsAndActions': {
              width: '100%',
              flexDirection: 'column',
              gap: spacing(1),
              '& > .details': { gap: spacing(1), '& > * > .detailsImg': { width: 30, height: 30 } },
              '& > .actions': {
                width: '100%',
                '& > *': { width: '100%', fontSize: 13, padding: '4px 10px' },
              },
            },
          },
        };
        return {
          margin: spacing(1),
          backgroundColor: palette.secondary.main,
          color: palette.secondary.contrastText,
          borderRadius: shape.borderRadiusSm,
          padding: spacing(3),
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: spacing(3),
          position: 'relative',
          '& > .closeBtn': {
            position: 'absolute',
            color: 'inherit',
            right: spacing(3),
            top: spacing(2),
          },
          '& > .ssImage': {
            width: 75,
            height: 75,
            borderRadius: shape.borderRadiusSm,
          },
          '& > .info': {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: spacing(1),
            '& > .detailsAndActions': {
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: spacing(2),
              '& > .details': {
                display: 'flex',
                flexWrap: 'wrap',
                gap: spacing(2),
                alignItems: 'center',
                '& > *': {
                  color: 'inherit',
                  textDecorationColor: 'inherit',
                  flexShrink: 0,
                  display: 'flex',
                  gap: spacing(1),
                  alignItems: 'center',
                  '& > .detailsImg': {
                    height: 40,
                    width: 40,
                    borderRadius: spacing(1),
                  },
                },
                [`& > .${dividerClasses.root}`]: {
                  height: 15,
                  color: 'inherit',
                  borderColor: 'currentColor',
                  // [`&:first-child, &:last-child, & + .${dividerClasses.root}`]: { display: 'none' },
                },
              },
              '& > .actions': {
                marginLeft: 'auto',
                alignSelf: 'flex-end',
                display: 'flex',
                gap: spacing(1),
              },
            },
          },
          '@media (max-height: 500px)': { display: 'none' },
          [breakpoints.down(800)]: mobileCss,
          '@container (max-width: 800px)': mobileCss,
        };
      }}
    >
      <IconButton className="closeBtn" onClick={onClose} size="small">
        <FontAwesomeIcon icon={faClose} />
      </IconButton>
      {hasMultipleTypes ? renderMultiTypes() : renderSingleType()}
    </div>
  );

  function renderMultiTypes() {
    return (
      <>
        <div className="info">
          <Typography
            className="title"
            variant="h5"
          >{`We have more help for ${assessment ? `your ${course.code} ${assessment.name}` : course.code}`}</Typography>
          <div className="detailsAndActions">
            <div className="details">
              {ssTypes.map((type, i) => {
                const typeConfig = STUDY_SESSION_TYPES[type];
                return (
                  <Fragment key={type}>
                    <MuiLink
                      component={Link}
                      onClick={() => gradeBoosterBannerClicked(course, type)}
                      to={
                        studySessionTypes[type].length > 1 ?
                          allBoostersPath
                        : studentCoursePageStudySessionPath(course, studySessionTypes[type][0])
                      }
                    >
                      <img
                        alt={typeConfig.label}
                        className="detailsImg"
                        css={({ palette }) => ({
                          backgroundColor: palette[typeConfig.color].light,
                        })}
                        src={typeConfig.image}
                      />
                      <Typography variant="subtitle1">
                        {pluralize(studySessionTypes[type].length, typeConfig.label, true)}
                      </Typography>
                    </MuiLink>
                    {i !== ssTypes.length - 1 && <Divider orientation="vertical" />}
                  </Fragment>
                );
              })}
            </div>
            <div className="actions">
              <CustomColorButton
                color="white_secondary"
                component={Link}
                nativeButton={false}
                onClick={() => gradeBoosterBannerClicked(course, 'view_all')}
                size="large"
                to={allBoostersPath}
                variant="contained"
              >
                {`View All ${assessment ? assessment.name : ''} Grade Boosters`}
              </CustomColorButton>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderSingleType() {
    const ssType = ssTypes[0];
    const isWeekly = ssType === 'weekly_tutorial';
    const ss =
      (isWeekly && studySessions.find(ss => ss.price)) ||
      (assessment && studySessions.find(ss => ss.assessment_id === assessment.id)) ||
      studySessions[0];
    const typeConfig = STUDY_SESSION_TYPES[ssType];
    const instructor = getStudySessionInstructor(ss);

    return (
      <>
        <img
          alt={typeConfig.label}
          className="ssImage"
          css={({ palette }) => ({ backgroundColor: palette[typeConfig.color].light })}
          src={typeConfig.image}
        />
        <div className="info">
          <Typography className="title" variant="h5">
            {ss.title}
          </Typography>
          <div className="detailsAndActions">
            <div className="details">
              {
                /* eslint-disable @eslint-react/no-missing-key */
                [
                  instructor && (
                    <div>
                      <MuiAvatar size={24} src="thumb" user={instructor} />
                      <Typography>{getUserDisplayName(instructor)}</Typography>
                    </div>
                  ),
                  ...(isWeekly ?
                    [
                      <div>
                        {pluralize(
                          studySessions.reduce((mem, ss) => mem + ss.dates.length, 0),
                          'Session',
                        )}
                      </div>,
                    ]
                  : renderNonWeekliesDetails(ss)),
                ]
                  .filter(Boolean)
                  .map((node, i, a) => (
                    <Fragment key={i}>
                      {node}
                      {i !== a.length - 1 && <Divider orientation="vertical" />}
                    </Fragment>
                  ))
                /* eslint-enable @eslint-react/no-missing-key */
              }
            </div>
            <div className="actions">
              <CustomColorButton
                color="white_secondary"
                component={Link}
                nativeButton={false}
                onClick={() => gradeBoosterBannerClicked(course, 'learn_more')}
                size="large"
                to={studySessions.length > 1 ? allBoostersPath : studentCoursePageStudySessionPath(course, ss)}
                variant="contained"
              >
                Learn More
              </CustomColorButton>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderNonWeekliesDetails(ss) {
    const startTime = getStudySessionStartTime(ss);

    const isPast = new Date(startTime) < new Date();

    const recordings = isPast && getAttachmentTypes(ss, 'all').recordings;

    const prefix =
      ss.disable_zoom ?
        isPast && recordings?.length ?
          'Released'
        : 'Releases'
      : isPast ? 'Recorded'
      : 'Live';

    const datePart =
      isPast ?
        ss.disable_zoom && !recordings?.length ?
          'Soon'
        : `${durationBetweenDates(startTime, null, { addSuffix: true })}`
      : `${formatDate(startTime, 'upcomingDateShort')}, ${formatDate(startTime, 'time')}`;

    /* eslint-disable @eslint-react/no-missing-key -- key not needed because will be wrapped in Fragment with key */
    return [
      <div>
        <FontAwesomeIcon icon={faHourglassClock} />
        <Typography>
          {pluralize(
            ...approximateDuration(
              recordings?.length ?
                recordings.reduce((mem, a) => mem + a.duration, 0)
              : ss.dates.reduce((mem, ssd) => mem + (new Date(ssd.end_time) - new Date(ssd.start_time)), 0) / 1000,
              { excludeDays: true, shortName: true },
            ),
          )}
        </Typography>
      </div>,
      <div>
        <FontAwesomeIcon icon={faCalendar} />
        <Typography>{`${prefix}: ${datePart}`}</Typography>
      </div>,
    ];
    /* eslint-enable @eslint-react/no-missing-key */
  }
}
function gradeBoosterBannerClicked(course, cta) {
  trackCustomEvent('Grade-Booster Banner Clicked', {
    course_id: course.id,
    course_code: course.code,
    school_id: course.school_id,
    school_name: course.school?.name,
    cta_text: cta,
  });
}
